<template>
  <div class="service-offerings container container--page-content">
    <div class="service-offerings__top flex flex-col gap-6 pb-14 lg:flex-row lg:justify-between lg:gap-8 lg:pb-20">
      <div class="service-offerings__header flex flex-col gap-6 lg:gap-4">
        <h2>{{ component.serviceOfferingsTitle }}</h2>
        <div
          v-if="component.serviceOfferingsDescription"
          v-html="component.serviceOfferingsDescription"
          class="text-medium-normal"
        />
      </div>
      <SharedPicture
        width="256"
        height="144"
        format="webp"
        :quality="80"
        :media="component.titleImage"
        class="service-offerings__image hidden lg:block lg:h-36 !w-[256px] aspect-[698/393]"
        imageClass="aspect-[698/393]"
      />
      <SharedPicture
        width="229"
        height="129"
        format="webp"
        :quality="80"
        :media="component.titleImage"
        class="service-offerings__image block lg:hidden !h-[129px] aspect-[698/393] !w-[229px] ml-2"
        imageClass="aspect-[698/393]"
      />
    </div>

    <div
      class="service-offerings__content grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-14 xl:grid-cols-[60%_40%] xl:overflow-x-hidden"
    >
      <ul
        ref="servicesListRef"
        class="service-offerings__services grid grid-cols-1 gap-6 mx-auto max-w-[400px] md:max-w-none md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 h-min"
      >
        <li
          v-for="(service, i) in component.services"
          :key="i"
          class="service-offerings__service flex gap-4 items-start p-3 service-offerings__service--enter rounded-[4px] border border-accent"
          :style="{ '--animation-delay': `${i * 100}ms` }"
        >
          <SharedPicture
            :media="service.icon"
            class="service-offerings__service-image block !size-12 !min-w-12 m-2"
            width="48"
            height="48"
            :quality="80"
            format="webp"
          />
          <div class="service-offerings__service-content flex flex-col gap-2">
            <div class="flex flex-col justify-end">
              <div class="h6 uppercase">{{ service.title }}</div>
            </div>
            <div class="flex flex-col justify-start">
              <div v-if="service.description" v-html="service.description" class="text-small-normal" />
            </div>
          </div>
        </li>
      </ul>
      <SharedPicture :media="component.serviceImage" class="service-offerings__service-image" 
        width="500"
        height="900"
        format="webp"
        :quality="80"
      />
    </div>

    <div
      v-if="component.serviceOfferingsCtas?.length"
      class="service-offerings__ctas flex flex-col items-center pt-14 lg:pt-20"
    >
      <SharedButton
        v-for="(cta, i) in component.serviceOfferingsCtas"
        :key="i"
        :label="cta.text || ''"
        :to="cta.target"
        :variant="cta.type"
        class="w-max"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
  import type { GraphQLServiceOfferings } from '~/types/graphql';
  import { useElementVisibility } from '@vueuse/core';

  const props = defineProps<{
    component: GraphQLServiceOfferings;
    index: number;
  }>();

  const servicesListRef = useTemplateRef<HTMLElement | null>('servicesListRef');
  const isVisible = useElementVisibility(servicesListRef, {
    rootMargin: '0px 0px -100px 0px',
  });

  // Handle animation on mount
  onMounted(() => {
    if (!servicesListRef.value) return;

    // If element is already visible, trigger animation after delay
    if (isVisible.value) {
      setTimeout(() => {
        const serviceItems = servicesListRef.value?.querySelectorAll('.service-offerings__service');
        serviceItems?.forEach(item => {
          item.classList.add('service-offerings__service--visible');
        });
      }, 500); // 0.5s delay for parent component animation
      return;
    }

    // Add inactive class for animation if element is not visible
    const serviceItems = servicesListRef.value?.querySelectorAll('.service-offerings__service');
    serviceItems?.forEach(item => {
      item.classList.add('service-offerings__service--inactive');
    });
  });

  // Watch for visibility changes and trigger animation
  watch(isVisible, visible => {
    if (visible && servicesListRef.value) {
      setTimeout(() => {
        const serviceItems = servicesListRef.value?.querySelectorAll('.service-offerings__service');
        serviceItems?.forEach(item => {
          item.classList.remove('service-offerings__service--inactive');
          item.classList.add('service-offerings__service--visible');
        });
      }, 500); // 0.5s delay for parent component animation
    }
  });
</script>

<style lang="scss" scoped>
  .service-offerings__service {
    &--enter {
      opacity: 0;
      transition: opacity 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
      transition-delay: var(--animation-delay, 0ms);
    }

    &--inactive {
      opacity: 0;
    }

    &--visible {
      opacity: 1;
    }
  }
</style>
